import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Edit, Trash2, FolderPlus, MoveHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ManagingPostsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Managing Posts</h1>
        <p className="text-lg text-gray-700 mb-6">
          Learn how to organize, edit, and manage your generated blog posts efficiently.
        </p>
      </div>

      <div className="prose max-w-none">
        <h2>Post Management Features</h2>
        <p>
          AISEOBlogGenerator provides several tools to help you manage your growing library of content.
          These features are designed to help you stay organized and make the most of your generated posts.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <FolderPlus className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="text-lg font-semibold">Creating Post Groups</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            Post groups help you organize your content by topic, client, campaign, or any other category that makes sense for your workflow.
          </p>
          
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 p-3 rounded-md border">
              <strong>Step 1:</strong> Click the folder icon in the sidebar of your Posts page
            </div>
            <div className="bg-gray-50 p-3 rounded-md border">
              <strong>Step 2:</strong> Enter a name for your new group (e.g., "Social Media Posts")
            </div>
            <div className="bg-gray-50 p-3 rounded-md border">
              <strong>Step 3:</strong> Click "Create Group" to save it
            </div>
          </div>
          
          <div className="mt-6">
            <h4 className="font-medium text-base mb-2">Example Groups:</h4>
            <ul className="space-y-2 text-sm text-gray-600 list-disc pl-5">
              <li>Client Projects</li>
              <li>SEO Content</li>
              <li>Email Newsletters</li>
              <li>Product Announcements</li>
              <li>Case Studies</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <MoveHorizontal className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="text-lg font-semibold">Moving Posts Between Groups</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            You can easily organize your content by moving posts between different groups.
          </p>
          
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium text-base mb-2">Method 1: Drag and Drop</h4>
              <ol className="space-y-2 list-decimal pl-5 text-gray-600">
                <li>Click and hold on a post in your list</li>
                <li>Drag the post over to the desired group in the sidebar</li>
                <li>Release to drop the post into that group</li>
              </ol>
            </div>
            
            <div>
              <h4 className="font-medium text-base mb-2">Method 2: Move Option</h4>
              <ol className="space-y-2 list-decimal pl-5 text-gray-600">
                <li>Hover over a post in your list</li>
                <li>Click the "Move" or three-dot menu icon</li>
                <li>Select the destination group from the dropdown menu</li>
              </ol>
            </div>
          </div>
          
          <div className="mt-6 bg-yellow-50 p-4 rounded-md border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Moving a post to a new group doesn't create a copy. It simply recategorizes the post.
            </p>
          </div>
        </div>
        
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Edit className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="text-lg font-semibold">Post Operations</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            Several operations can be performed on individual posts to help you manage your content.
          </p>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 border rounded-md">
              <button className="bg-gray-100 p-1 rounded" disabled>
                <Edit className="w-4 h-4 text-gray-700" />
              </button>
              <div>
                <h4 className="font-medium">Re-generate</h4>
                <p className="text-sm text-gray-600">Create a new version of a post with similar parameters</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 border rounded-md">
              <button className="bg-gray-100 p-1 rounded" disabled>
                <Edit className="w-4 h-4 text-gray-700" />
              </button>
              <div>
                <h4 className="font-medium">Copy as Reference</h4>
                <p className="text-sm text-gray-600">Use an existing post's parameters as a starting point for a new post</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 border rounded-md">
              <button className="bg-red-100 p-1 rounded" disabled>
                <Trash2 className="w-4 h-4 text-red-700" />
              </button>
              <div>
                <h4 className="font-medium">Delete</h4>
                <p className="text-sm text-gray-600">Permanently remove a post from your account</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <FolderPlus className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="text-lg font-semibold">Post Storage</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            All generated posts are automatically saved to your account and count toward your monthly usage. Here's what you need to know about post storage:
          </p>
          
          <div className="space-y-3 text-sm text-gray-600">
            <div className="p-3 border rounded-md">
              <strong>Storage Duration:</strong> Posts remain in your account indefinitely, even after your subscription period.
            </div>
            
            <div className="p-3 border rounded-md">
              <strong>Monthly Limits:</strong> Each subscription tier has a monthly limit on new posts:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Free: 3 posts/month</li>
                <li>Pro: 15 posts/month</li>
                <li>Enterprise: 100 posts/month</li>
                <li>Enterprise VIP: 1,000 posts/month</li>
              </ul>
            </div>
            
            <div className="p-3 border rounded-md">
              <strong>Usage Reset:</strong> Your monthly usage count resets on the 1st day of each calendar month.
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border rounded-lg p-6 mt-8">
        <h3 className="text-xl font-bold mb-4">Managing Your Content Archive</h3>
        
        <p className="text-gray-700 mb-6">
          As your content library grows, consider these best practices for effective management:
        </p>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="bg-green-100 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-green-700 font-medium">1</span>
            </div>
            <div>
              <h4 className="font-medium">Create a Group Structure</h4>
              <p className="text-sm text-gray-600">
                Build a logical hierarchy of groups that reflects your content organization needs. Consider grouping by topic, funnel stage, or content type.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="bg-green-100 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-green-700 font-medium">2</span>
            </div>
            <div>
              <h4 className="font-medium">Use Descriptive Titles</h4>
              <p className="text-sm text-gray-600">
                Give your posts clear, descriptive titles that make them easy to find later. Include key information like topic and content type.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="bg-green-100 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-green-700 font-medium">3</span>
            </div>
            <div>
              <h4 className="font-medium">Regularly Archive or Delete Unused Content</h4>
              <p className="text-sm text-gray-600">
                To keep your workspace manageable, periodically review your posts and remove those you no longer need.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="bg-green-100 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-green-700 font-medium">4</span>
            </div>
            <div>
              <h4 className="font-medium">Export Important Content</h4>
              <p className="text-sm text-gray-600">
                For critical content, consider exporting to an external system like Google Docs or Notion for additional backup.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/posts">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Blog Posts
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/posts/export">
            Next: Export Options
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}